import React, { useState, useCallback, useMemo } from 'react';
import {
    ChevronRight,
    ChevronDown,
    Folder,
    FolderOpen,
    FileText,
    Layers,
    Search,
    X,
} from 'lucide-react';
import type { ExpenditureActivityTreeNode } from '../types';

interface ExpenditureActivityTreeProps {
    data: ExpenditureActivityTreeNode[];
    isLoading?: boolean;
    onNodeClick?: (node: ExpenditureActivityTreeNode) => void;
}

interface TreeNodeProps {
    node: ExpenditureActivityTreeNode;
    level: number;
    expandedNodes: Set<number>;
    onToggle: (nodeId: number) => void;
    onNodeClick?: (node: ExpenditureActivityTreeNode) => void;
    searchQuery: string;
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2,
    }).format(amount);
};

// Get icon based on level
const getNodeIcon = (level: number, isExpanded: boolean, hasChildren: boolean) => {
    if (!hasChildren) {
        return <FileText className="h-4 w-4 text-gray-400" />;
    }
    if (level === 0) {
        return isExpanded ? (
            <FolderOpen className="h-5 w-5 text-[#8B4513]" />
        ) : (
            <Folder className="h-5 w-5 text-[#8B4513]" />
        );
    }
    if (level === 1) {
        return isExpanded ? (
            <FolderOpen className="h-4 w-4 text-yellow-500" />
        ) : (
            <Folder className="h-4 w-4 text-yellow-500" />
        );
    }
    if (level === 2) {
        return isExpanded ? (
            <FolderOpen className="h-4 w-4 text-green-500" />
        ) : (
            <Folder className="h-4 w-4 text-green-500" />
        );
    }
    return isExpanded ? (
        <FolderOpen className="h-4 w-4 text-purple-500" />
    ) : (
        <Folder className="h-4 w-4 text-purple-500" />
    );
};

// Get background and border colors based on level
const getNodeStyles = (level: number) => {
    const baseStyles = 'transition-all duration-200 rounded-lg bg-gray-50';

    switch (level) {
        case 0:
            return `${baseStyles} border-l-4 border-l-[#8B4513]`;
        case 1:
            return `${baseStyles} border-l-4 border-l-yellow-500`;
        case 2:
            return `${baseStyles} border-l-4 border-l-green-500`;
        default:
            return `${baseStyles} border-l-4 border-l-purple-500`;
    }
};

// Get type badge based on level
const getTypeBadge = (level: number) => {
    const labels = ['HEAD', 'SUBHEAD', 'ACTIVITY', 'SUBACTIVITY'];
    const classes = [
        'bg-[#8B4513] text-white',
        'bg-yellow-500 text-white',
        'bg-green-500 text-white',
        'bg-purple-500 text-white',
    ];

    return (
        <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full ${classes[level] || classes[3]}`}>
            {labels[level] || labels[3]}
        </span>
    );
};

// Highlight search text in title
const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 px-0.5 rounded">
                {part}
            </mark>
        ) : (
            part
        )
    );
};

// Check if node or any child matches search
const nodeMatchesSearch = (node: ExpenditureActivityTreeNode, query: string): boolean => {
    if (!query.trim()) return true;

    const lowerQuery = query.toLowerCase();
    if (node.title.toLowerCase().includes(lowerQuery)) return true;

    return node.children.some((child) => nodeMatchesSearch(child, query));
};

// Tree Node Component
const TreeNode: React.FC<TreeNodeProps> = ({
    node,
    level,
    expandedNodes,
    onToggle,
    onNodeClick,
    searchQuery,
}) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);

    // Filter visible children based on search
    const visibleChildren = useMemo(() => {
        if (!searchQuery.trim()) return node.children;
        return node.children.filter((child) => nodeMatchesSearch(child, searchQuery));
    }, [node.children, searchQuery]);

    // Check if this node matches search
    const matchesSearch =
        !searchQuery.trim() || node.title.toLowerCase().includes(searchQuery.toLowerCase());

    // Don't render if doesn't match and has no matching children
    if (searchQuery.trim() && !matchesSearch && visibleChildren.length === 0) {
        return null;
    }

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) {
            onToggle(node.id);
        }
    };

    const handleNodeClick = () => {
        if (onNodeClick) {
            onNodeClick(node);
        }
    };

    const paddingLeft = level * 24;

    return (
        <div className="animate-fadeIn relative">
            <div className="flex relative">
                {/* Visual Guide Line for hierarchy */}
                {level > 0 && (
                    <div
                        className="absolute left-0 top-0 bottom-0 border-l border-dashed border-gray-300"
                        style={{ left: paddingLeft - 12 }}
                    />
                )}

                <div className="flex-1 min-w-0" style={{ marginLeft: paddingLeft }}>
                    {/* Node Card */}
                    <div
                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 cursor-pointer ${getNodeStyles(level)} mb-2 relative hover:shadow-md transition-shadow`}
                        onClick={handleNodeClick}
                    >
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                            {/* Expand/Collapse Button */}
                            <button
                                onClick={handleToggle}
                                className={`flex-shrink-0 mt-1 p-0.5 rounded hover:bg-black/5 transition-colors ${hasChildren ? 'visible' : 'invisible'
                                    }`}
                            >
                                {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                            </button>

                            {/* Icon */}
                            <div className="flex-shrink-0 mt-1">
                                {getNodeIcon(level, isExpanded, hasChildren)}
                            </div>

                            {/* Title & Badge */}
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    {getTypeBadge(level)}
                                    <span className="font-semibold text-gray-800 text-sm break-words">
                                        {highlightText(node.title, searchQuery)}
                                    </span>
                                </div>
                                {hasChildren && (
                                    <p className="text-xs text-gray-500">
                                        {node.children.length} {node.children.length === 1 ? 'item' : 'items'}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Financial Stats */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 text-xs sm:text-sm">
                            <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Sanctioned</p>
                                <p className="font-bold text-orange-600">{formatCurrency(node.sanTotal)}</p>
                                <div className="flex gap-2 mt-0.5 text-[10px]">
                                    <span className="text-cyan-600">R: {formatCurrency(node.sanRec)}</span>
                                    <span className="text-rose-600">NR: {formatCurrency(node.sanNonRec)}</span>
                                </div>
                            </div>
                            <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Spent</p>
                                <p className="font-bold text-green-600">{formatCurrency(node.spentTotal)}</p>
                                <div className="flex gap-2 mt-0.5 text-[10px]">
                                    <span className="text-cyan-600">R: {formatCurrency(node.spentRec)}</span>
                                    <span className="text-rose-600">NR: {formatCurrency(node.spentNonRec)}</span>
                                </div>
                            </div>
                            <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Utilization</p>
                                <p className="font-bold text-blue-600">
                                    {node.sanTotal > 0 ? ((node.spentTotal / node.sanTotal) * 100).toFixed(1) : '0.0'}%
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Children */}
                    {isExpanded && hasChildren && (
                        <div className="space-y-1">
                            {visibleChildren.map((child) => (
                                <TreeNode
                                    key={child.id}
                                    node={child}
                                    level={level + 1}
                                    expandedNodes={expandedNodes}
                                    onToggle={onToggle}
                                    onNodeClick={onNodeClick}
                                    searchQuery={searchQuery}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main Component
const ExpenditureActivityTree: React.FC<ExpenditureActivityTreeProps> = ({
    data,
    isLoading = false,
    onNodeClick,
}) => {
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const handleToggle = useCallback((nodeId: number) => {
        setExpandedNodes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    }, []);

    const handleExpandAll = useCallback(() => {
        const getAllNodeIds = (nodes: ExpenditureActivityTreeNode[]): number[] => {
            return nodes.flatMap((node) => [node.id, ...getAllNodeIds(node.children)]);
        };
        setExpandedNodes(new Set(getAllNodeIds(data)));
    }, [data]);

    const handleCollapseAll = useCallback(() => {
        setExpandedNodes(new Set());
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchQuery('');
    }, []);

    // Filter visible data based on search
    const visibleData = useMemo(() => {
        if (!searchQuery.trim()) return data;
        return data.filter((node) => nodeMatchesSearch(node, searchQuery));
    }, [data, searchQuery]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Layers className="h-5 w-5" />
                        Expenditure Activity Tree
                    </h3>
                    <div className="flex gap-2">
                        <button
                            onClick={handleExpandAll}
                            className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Expand All
                        </button>
                        <button
                            onClick={handleCollapseAll}
                            className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Collapse All
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="mt-3 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search activities..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                    />
                    {searchQuery && (
                        <button
                            onClick={handleClearSearch}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X className="h-4 w-4 text-gray-500" />
                        </button>
                    )}
                </div>
            </div>

            {/* Tree Content */}
            <div className="p-4 max-h-[600px] overflow-y-auto">
                {visibleData.length > 0 ? (
                    <div className="space-y-1">
                        {visibleData.map((node) => (
                            <TreeNode
                                key={node.id}
                                node={node}
                                level={0}
                                expandedNodes={expandedNodes}
                                onToggle={handleToggle}
                                onNodeClick={onNodeClick}
                                searchQuery={searchQuery}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-lg font-medium">No results found</p>
                        <p className="text-sm mt-1">Try adjusting your search query</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpenditureActivityTree;
